import React, { memo, ReactNode, useContext, useState } from 'react';
import { CustomActionContainer } from './FiltersContainer';
import TriggerPopover from '../../components/connectedForm/TriggerPopover';
import DeprecatedTooltip from '../../components/connectedForm/Tooltip/DeprecatedTooltip';
import { Button } from '../connectedForm/Button';
import ViewContext from '../Context/ViewContext';
import GridToolbarWorkspaceSearch from '../GridToolbar/GridToolbarWorkspaceSearch';
import GridToolbarWorkspaceRowCounter from '../GridToolbar/GridToolbarWorkspaceRowCounter';
import GridToolbarWorkspaceFiltersContainer from '../GridToolbar/GridToolbarWorkspaceFiltersContainer';
import GridToolbarWorkspaceColumnSelector from '../GridToolbar/GridToolbarWorkspaceColumnSelector';
import GridToolbarWorkspaceRefreshButton from '../GridToolbar/GridToolbarWorkspaceRefreshButton';
import { makeStyles } from '@fluentui/react-components';
import { useGridToolbarItemStyles } from '../GridToolbar/gridToolbarStyles';

const ToolbarSwitchView = ({
  detailView,
  embeddedView,
  tabEnable,
  switchView,
}: {
  detailView: ReactNode;
  embeddedView: boolean;
  tabEnable: boolean;
  switchView: (type: 'form') => void;
}) => {
  const styles = useGridToolbarItemStyles();

  if (!detailView) return null;
  return embeddedView ? null : (
    <div className={styles.switchView}>
      <TriggerPopover flexBox={false} popover={<DeprecatedTooltip position="view-toolbar" value="Form view" />}>
        <a
          className={styles.switchViewLink}
          href="#"
          tabIndex={tabEnable ? 1 : -1}
          data-testid="view_quilt"
          onClick={(e) => {
            e.preventDefault();
            switchView('form');
          }}
        >
          <i className="iconLarge">view_quilt</i>
        </a>
      </TriggerPopover>
    </div>
  );
};

type IViewToolbarProps = {
  className?: string;
  detailView: boolean;
  tabEnable: boolean;
  onSwitchView: (view: string) => void;
  viewController?: ReactNode;
  embeddedView: boolean;
  hideColumnSelector: boolean;
  hideViewController: boolean;
  includeGenericSearch: boolean;
  onBack?: () => void;
  backButtonRequired?: boolean;

  //tableNam, count & selectedCount used for memoization of ViewToolbar only.
  //May eventually become unnecessary (If all the other props become appropriately memoized).
  tableName: string;
  count: number;
  selectedCount: number;

  /**
   * Action buttons pass-through from the Workspace (I.e. This is <WorkspaceActionButtons/>)
   * TODO - Migrate to building the toolbar with GridToolbar components.
   */
  actionContext?: ReactNode;

  /**
   * Whether or not actionContext should be displayed in the Toolbar.
   * TODO - Migrate to building the toolbar with GridToolbar components.
   */
  embeddedViewActions?: boolean;
  'data-testid': string;
  customToolbarAction?: ReactNode;
  onResetClick?: () => void;

  /**
   * Passed through to FilterContainer - Prevents call to rowSelect with empty selection when the filter is changed.
   */
  preventSelectionResetOnFilterChange?: boolean;
};

const useStyles = makeStyles({
  toolbar: {
    minHeight: '42px',
    margin: '12px 0px 12px 0px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    border: '1px solid var(--grey40)',
    backgroundColor: '#fff',
  },
  viewToolbarLeft: {
    display: 'flex',
  },
  viewToolbarRight: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
});

const ViewToolbar = memo(
  ({
    viewController,
    hideViewController = false,
    embeddedView = false,
    onBack: onBackFromProps,
    onSwitchView,
    backButtonRequired,
    tabEnable,
    includeGenericSearch = true,
    hideColumnSelector = false,
    detailView,
    'data-testid': dataTestId,
    customToolbarAction,
    onResetClick,
    preventSelectionResetOnFilterChange = false,
  }: IViewToolbarProps) => {
    const [currentView, setCurrentView] = useState<string>('grid');
    const viewContext = useContext(ViewContext);
    const styles = useStyles();

    const switchView = (view: string) => {
      setCurrentView(view);
      onSwitchView(view);
    };

    const onBack = () => {
      onBackFromProps?.();
    };

    // Don't render if view has hideConfiguration set
    if (viewContext.currentView?.hideConfiguration) {
      return null;
    }

    let backButton: ReactNode = null;
    if (backButtonRequired) {
      backButton = (
        <Button data-testid={`close-${dataTestId}`} style={{ display: 'flex', order: 2 }} onClick={onBack}>
          Close
        </Button>
      );
    }

    return (
      <div className={styles.toolbar}>
        <div className={styles.viewToolbarLeft}>
          {includeGenericSearch ? <GridToolbarWorkspaceSearch oldAppearance={true} /> : null}
          <GridToolbarWorkspaceFiltersContainer
            preventSelectionResetOnFilterChange={preventSelectionResetOnFilterChange}
            tabEnable={tabEnable}
          />
          <GridToolbarWorkspaceRowCounter />
          {customToolbarAction && <CustomActionContainer>{customToolbarAction}</CustomActionContainer>}
        </div>
        <div className={styles.viewToolbarRight}>
          {backButton}
          <GridToolbarWorkspaceRefreshButton />
          {hideColumnSelector || currentView !== 'grid' ? null : (
            <GridToolbarWorkspaceColumnSelector onResetClick={onResetClick} tabEnable={tabEnable} />
          )}
          <ToolbarSwitchView
            detailView={detailView}
            embeddedView={embeddedView}
            tabEnable={tabEnable}
            switchView={switchView}
          />
          {embeddedView && !hideViewController ? viewController : null}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.tableName === nextProps.tableName &&
      prevProps.count === nextProps.count &&
      prevProps.selectedCount === nextProps.selectedCount
    );
  }
);

export default ViewToolbar;
